import React, {SetStateAction, useEffect, useMemo, useState} from "react";
import dayjs, { Dayjs } from "dayjs";
import {capitalize, Checkbox, CircularProgress, Divider, Grid, Stack, Typography, IconButton} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {useReferentialContext} from "../../Context";
import { ScrimGames } from "../../reckon-api";
import Box from "@mui/material/Box";
import {Title} from "../../NeonComponents/Texts";
import formatters from "../../NeonComponents/Formatters";
import {StatsBlock} from "../../NeonComponents/NeonStatsBlock";
import {NeonCard, NeonCardContent, NeonCardHeader} from "../../NeonComponents/NeonCards";
import {NeonDialog, NeonDialogTitle, NeonDialogContent, NeonDialogActions} from "../../NeonComponents/NeonDialog";
import {NeonButton} from "../../NeonComponents/Buttons";
import TableDisplayGrid, {rowStyle} from "../../components/datagrid/TableDisplayGrid";
import {ColumnTypes, field_name_to_column} from "../../components/datagrid/styled_datagrid";
import TeamIcon from "../../components/Icons/TeamIcon";
import WindowContainer from "../../components/layout/WindowContainer";
import AgentIcon from "../../components/Icons/AgentIcon";
import DecoratedMap from "../../components/Icons/DecoratedMap";
import {NeonGlassItem} from "../../NeonComponents/NeonGlassItem";
import { getNeonColor, scrollable } from "../../theme";
import DatePicker from "../../components/selectors/Shared/DateSelect";
import { getMapAsset } from "../../valorant-api/mapAssets";
import { MapBannerSelect } from "../../components/selectors/MapSelect";


export default function ScrimsMapStats(){
    const theme = useTheme();
    const neonColor = getNeonColor(theme);
    const ctx = useReferentialContext();
    const userTeam = ctx.user.team;
    const [from, setFrom] = useState(dayjs().subtract(3, "month"));
    const [until, setUntil] = useState(dayjs());
    const [tab, setTab] = useState(0);
    const [mapSelected, setMapSelected] = useState(ctx.mapsReferential[0]);

    const [games, setGames] = useState<ScrimGames[]>([]);
    const [selectedGameIds, setSelectedGameIds] = useState<Set<string>>(new Set());
    const [gamesLoading, setGamesLoading] = useState(true);

    const [side, setSide] = useState(undefined as string|undefined);
    const [economy, setEconomy] = useState(undefined as string|undefined);
    const [agent, setAgent] = useState(undefined as string|undefined);
    const [bombSite, setBombSite] = useState(undefined as string|undefined);
    const [defensePattern, setDefensePattern] = useState(undefined as string|undefined);
    const [attackPattern, setAttackPattern] = useState(undefined as string|undefined);

    useEffect(() => {
        const selectedMap = ctx.mapsReferential[tab];
        if (selectedMap) {
            setMapSelected(selectedMap);
        }
    }, [tab, ctx.mapsReferential]);

    useEffect(() => {
        if (!mapSelected && ctx.mapsReferential.length > 0) {
            setMapSelected(ctx.mapsReferential[0]);
        }
    }, [ctx.mapsReferential, mapSelected]);

    useEffect(() => {
        setGamesLoading(true);
        ctx.reckonApi.scrimGames
            .list({
                ordering: ["-date"],
                additional_filters: {
                    date__gte: from.toISOString(),
                    date__lt: until.toISOString(),
                },
            })
            .then((result) => {
                setGames(result);
                setSelectedGameIds(new Set(result.map((g) => g.id)));
                setGamesLoading(false);
            })
            .catch(() => setGamesLoading(false));
    }, [from, until]);

    const myGames = useMemo(
        () => games.filter((g) => g.team1 === userTeam || g.team2 === userTeam),
        [games, userTeam],
    );

    const mapsWithGames = useMemo(
        () => [...new Set(myGames.map((g) => g.map).filter(Boolean))],
        [myGames],
    );

    const mapGames = useMemo(
        () => (mapSelected ? myGames.filter((g) => g.map === mapSelected.id) : myGames),
        [myGames, mapSelected],
    );

    useEffect(() => {
        if (!mapsWithGames.length || !mapSelected) return;
        if (mapsWithGames.includes(mapSelected.id)) return;
        const idx = ctx.mapsReferential.findIndex((m) => mapsWithGames.includes(m.id));
        if (idx >= 0) setTab(idx);
    }, [mapsWithGames, mapSelected, ctx.mapsReferential]);

    const activeGameIds = useMemo(
        () => myGames.filter((g) => selectedGameIds.has(g.id)).map((g) => g.id),
        [myGames, selectedGameIds],
    );

    const selectedGamesKey = activeGameIds.join(",");

    const toggleGame = (id: string) => {
        setSelectedGameIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const computeParamObject = () => {
        if (!mapSelected || activeGameIds.length === 0) return null;

        const watermark = from.toISOString() + until.toISOString() +
            mapSelected.id + userTeam + side + economy + agent + bombSite + defensePattern + attackPattern + selectedGamesKey;

        return {
            map: mapSelected.id,
            side: side,
            bomb_site: bombSite,
            team1: userTeam,
            agent: agent,
            round_eco_type_no_bonus: economy,
            defense_pattern_short: defensePattern,
            attack_pattern_short: attackPattern,
            additional_filters: {
                date__gte: from.toISOString(),
                date__lt: until.toISOString(),
                game__in: selectedGamesKey,
            },
            watermark
        };
    };

    const [paramObject, setParamObject] = useState<any>(() => computeParamObject() || {});

    useEffect(() => {
        const newParamObject = computeParamObject();
        setParamObject(newParamObject || {});
    }, [mapSelected, from, until, userTeam, side, economy, agent, bombSite, defensePattern, attackPattern, selectedGamesKey]);

    const [detailDialog, setDetailDialog] = useState<{title: string; rows: any[]; groupby: string} | null>(null);
    const openDetail = (title: string, rows: any[], groupby: string) => setDetailDialog({title, rows, groupby});

    const METRIC_CODES = ['acs','kpr','dpr','kills_total','deaths_total','assists_total','kast','adr','first_blood_total','first_deaths_total'];
    const detailMetrics = ctx.metricsReferential.filter(m => METRIC_CODES.includes(m.code));

    const buildGroupbyColumn = (groupby: string) => {
        if (groupby === 'team1') {
            return {
                field: 'team1',
                headerName: 'Team',
                minWidth: 50,
                maxWidth: 50,
                align: 'center' as const,
                headerAlign: 'center' as const,
                renderCell: (params: any) => params.value ? (
                    <TeamIcon team_name={params.value} sx={{width: 28, height: 28}}/>
                ) : '',
            };
        }
        if (groupby === 'agent') {
            return {
                field: 'agent',
                headerName: 'Agent',
                minWidth: 60,
                renderCell: (params: any) => params.value ? (
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <AgentIcon agent_name={params.value}/>
                        <Typography variant="body2">{params.value}</Typography>
                    </Stack>
                ) : '',
            };
        }
        if (field_name_to_column[groupby]) return field_name_to_column[groupby];
        return ColumnTypes.string(groupby, capitalize(groupby.replace(/_/g, ' ')));
    };

    const hasStats = Boolean(paramObject.map && paramObject.additional_filters);

    if (!mapSelected) {
        return <WindowContainer spacing={2}>Loading maps...</WindowContainer>;
    }

    const mapActiveCount = mapGames.filter((g) => selectedGameIds.has(g.id)).length;

    return <WindowContainer spacing={2}>
            <Stack direction="row" spacing={2} sx={{alignItems: "center"}}>
                <DatePicker value={from} onChange={(v: Dayjs | null) => v && setFrom(v)} label="From" />
                <DatePicker value={until} onChange={(v: Dayjs | null) => v && setUntil(v)} label="Until" />
            </Stack>

            <MapBannerSelect
                value={mapSelected}
                updateValue={(m) => {
                    if (!m) return;
                    const idx = ctx.mapsReferential.findIndex((map) => map.id === m.id);
                    if (idx >= 0) setTab(idx);
                }}
                mapsWithGames={mapsWithGames}
            />

    <Grid container columns={12} sx={{height:'84%'}} spacing={1}>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
                <NeonCard
                    sx={{
                        height:'100%',
                        minHeight:'470px',
                        paddingBot:0,
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            inset: 0,
                            zIndex: 0,
                            pointerEvents: 'none',
                            backgroundImage: `url(${getMapAsset(mapSelected.id, 'stylizedBackgroundImage')})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            opacity: 0.55,
                            WebkitMaskImage:
                                'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                            maskImage:
                                'radial-gradient(ellipse 82% 78% at 50% 45%, #000 0%, #000 48%, transparent 88%)',
                        },
                    }}
                >
                    <NeonCardHeader title={capitalize(mapSelected.id)}/>
                    <NeonCardContent sx={{width:'100%', height:'85%'}}>
                        <DecoratedMap map={mapSelected}/>
                    </NeonCardContent>
                </NeonCard>
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
                {hasStats && <StatsByBombSite paramObject={paramObject} bombSite={bombSite} setBombSite={setBombSite} onShowDetails={openDetail}/>}
                <NeonCard sx={{flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column'}}>
                    <NeonCardHeader
                        title="Games"
                        action={
                            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
                                <Typography variant="caption" sx={{ color: "text.secondary", whiteSpace: "nowrap" }}>
                                    {mapActiveCount}/{mapGames.length}
                                </Typography>
                                <NeonButton
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSelectedGameIds((prev) => {
                                        const next = new Set(prev);
                                        mapGames.forEach((g) => next.add(g.id));
                                        return next;
                                    })}
                                    sx={{ fontSize: "0.7rem", px: 1, py: 0.25, minWidth: 0 }}
                                >
                                    All
                                </NeonButton>
                                <NeonButton
                                    variant="outlined"
                                    size="small"
                                    onClick={() => setSelectedGameIds((prev) => {
                                        const next = new Set(prev);
                                        mapGames.forEach((g) => next.delete(g.id));
                                        return next;
                                    })}
                                    sx={{ fontSize: "0.7rem", px: 1, py: 0.25, minWidth: 0 }}
                                >
                                    None
                                </NeonButton>
                            </Stack>
                        }
                        sx={{ pb: 0 }}
                    />
                    <NeonCardContent sx={{ flexDirection: "column", gap: 0, pt: 1, flex: 1, minHeight: 0, ...scrollable }}>
                        {gamesLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress size={28} sx={{ color: neonColor }} />
                            </Box>
                        ) : (
                            <>
                                {mapGames.map((g) => {
                                    const won = g.winner === userTeam;
                                    const isTeam1 = g.team1 === userTeam;
                                    const opponent = isTeam1 ? g.team2 : g.team1;
                                    const myScore = isTeam1 ? g.score_team1 : g.score_team2;
                                    const enemyScore = isTeam1 ? g.score_team2 : g.score_team1;
                                    const checked = selectedGameIds.has(g.id);

                                    return (
                                        <Box key={g.id}>
                                            <Stack
                                                direction="row"
                                                onClick={() => toggleGame(g.id)}
                                                sx={{
                                                    alignItems: "center",
                                                    py: 0.75,
                                                    px: 1,
                                                    borderLeft: `3px solid ${won ? "#4caf50" : "#f44336"}`,
                                                    borderRadius: 0.5,
                                                    cursor: "pointer",
                                                    opacity: checked ? 1 : 0.4,
                                                    transition: "opacity 0.2s",
                                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.03)" },
                                                }}
                                                spacing={1}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    tabIndex={-1}
                                                    sx={{ color: neonColor, "&.Mui-checked": { color: neonColor }, p: 0 }}
                                                />
                                                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", flex: 1, justifyContent: "center" }}>
                                                    <TeamIcon team_name={userTeam} sx={{ width: 18, height: 18 }} />
                                                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: won ? "lightgreen" : "error.main" }}>
                                                        {myScore}
                                                    </Typography>
                                                    <Typography sx={{ color: "text.secondary", fontSize: "0.75rem" }}>-</Typography>
                                                    <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", color: !won ? "lightgreen" : "error.main" }}>
                                                        {enemyScore}
                                                    </Typography>
                                                    <TeamIcon team_name={opponent} sx={{ width: 18, height: 18 }} />
                                                </Stack>
                                                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem", whiteSpace: "nowrap" }}>
                                                    {dayjs(g.date).format("MMM D")}
                                                </Typography>
                                            </Stack>
                                            <Divider sx={{ opacity: 0.15 }} />
                                        </Box>
                                    );
                                })}
                                {mapGames.length === 0 && (
                                    <Typography sx={{ color: "text.secondary", py: 2, textAlign: "center" }}>
                                        No games found.
                                    </Typography>
                                )}
                            </>
                        )}
                    </NeonCardContent>
                </NeonCard>
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {hasStats && <StatsBySide paramObject={paramObject} side={side} setSide={setSide} onShowDetails={openDetail}/>}
            {hasStats && <StatsByEcoType paramObject={paramObject} economy={economy} setEconomy={setEconomy} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {hasStats && <StatsByAgent paramObject={paramObject} agent={agent} setAgent={setAgent} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {hasStats && <StatsByDefensivePattern paramObject={paramObject} defensePattern={defensePattern} setDefensePattern={setDefensePattern} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
        <Grid size={3} sx={{height:'100%'}}>
            <Stack direction={'column'} sx={{height:'100%'}} spacing={1}>
            {hasStats && <StatsByAttackingPattern paramObject={paramObject} attackPattern={attackPattern} setAttackPattern={setAttackPattern} onShowDetails={openDetail}/>}
            </Stack>
        </Grid>
    </Grid>

        <NeonDialog
            open={detailDialog !== null}
            onClose={() => setDetailDialog(null)}
            maxWidth="lg"
            fullWidth
            PaperProps={{sx: {height: '70vh'}}}
        >
            <NeonDialogTitle>{detailDialog?.title ?? ''}</NeonDialogTitle>
            <NeonDialogContent sx={{p: 0, overflow: 'hidden'}}>
                {detailDialog && detailDialog.rows.length > 0 && (
                    <TableDisplayGrid
                        request={() => Promise.resolve(detailDialog.rows)}
                        display_all={false}
                        custom_columns={[
                            buildGroupbyColumn(detailDialog.groupby),
                            ColumnTypes.short_int('games', 'Games'),
                            ColumnTypes.short_int('rounds', 'Rounds'),
                            ColumnTypes.percentage('win__avg', 'Win%'),
                            ...detailMetrics.map(m =>
                                ColumnTypes[m.type ? m.type : 'string'](m.code, m.label, {description: m.desc})
                            ),
                        ]}
                        rowStyle={rowStyle.none}
                        sortBy="rounds"
                        paramObject={{watermark: detailDialog.title + detailDialog.rows.length}}
                    />
                )}
            </NeonDialogContent>
            <NeonDialogActions>
                <NeonButton variant="outlined" onClick={() => setDetailDialog(null)}>
                    Close
                </NeonButton>
            </NeonDialogActions>
        </NeonDialog>
    </WindowContainer>
}

interface StatsRowProps {
  stats: any;
  title?: React.ReactNode;
  decoration?: React.ReactNode;
  selected: boolean;
  onClick: () => void;
}

const StatsRow: React.FC<StatsRowProps> = React.memo(({ stats, title, decoration, selected, onClick }) => {
    return <Box sx={{width:'100%',alignItems:'center', justifyContent:'center', display:'flex'}}>
        <NeonGlassItem
            isClickable
            onClick={onClick}
            sx={{
                width:'90%',
                minHeight: '120px',
                position: 'relative',
                "&::before": selected
                    ? {
                        content: "''",
                        position: "absolute",
                        top: "15%",
                        bottom: "15%",
                        left: "0px",
                        width: "1px",
                        height: "70%",
                        background: "#ffffff",
                        boxShadow: `0px 0px 5px #fff, 0px 0px 20px #fff`,
                      }
                    : {},
            }}
        >
            <Grid container columns={4} spacing={1}>
                <Grid size={0.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {title}
                </Grid>
                <Grid size={3.5} sx={{alignItems:'center', justifyContent:'center', display:'flex'}}>
                    {decoration}
                </Grid>
                <Grid size={1}><StatsBlock label={'Games'} value={stats.games} metricCode="games" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'Rounds'} value={stats.rounds} metricCode="rounds" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'RWin%'} value={stats.win__avg} metricCode="win_avg" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KD'} value={stats.deaths_total ? stats.kills_total / stats.deaths_total : 0} metricCode="kd" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'Kast'} value={stats.kast} metricCode="kast" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'KPR'} value={stats.kpr} metricCode="kpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ADR'} value={stats.adr} metricCode="adr" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'DPR'} value={stats.dpr} metricCode="dpr" formatter={formatters.float2}/></Grid>
                <Grid size={1}><StatsBlock label={'ACS'} value={stats.acs} metricCode="acs" formatter={formatters.integer}/></Grid>
                <Grid size={1}><StatsBlock label={'FB%'} value={stats.rounds ? stats.first_blood_total / stats.rounds : 0} metricCode="fb_pct" formatter={formatters.percent}/></Grid>
                <Grid size={1}><StatsBlock label={'FD%'} value={stats.rounds ? stats.first_deaths_total / stats.rounds : 0} metricCode="fd_pct" formatter={formatters.percent}/></Grid>
            </Grid>
        </NeonGlassItem>
    </Box>
});

interface CarouselNavProps {
    currentIndex: number;
    totalItems: number;
    visibleCount: number;
    onPrev: () => void;
    onNext: () => void;
}

const CarouselNav: React.FC<CarouselNavProps> = ({ currentIndex, totalItems, visibleCount, onPrev, onNext }) => {
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex + visibleCount < totalItems;

    return (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mt: 1 }}>
            <IconButton
                onClick={onPrev}
                disabled={!canGoPrev}
                size="small"
                sx={{
                    color: canGoPrev ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 60, textAlign: 'center' }}>
                {currentIndex + 1}-{Math.min(currentIndex + visibleCount, totalItems)} / {totalItems}
            </Typography>
            <IconButton
                onClick={onNext}
                disabled={!canGoNext}
                size="small"
                sx={{
                    color: canGoNext ? 'white' : 'rgba(255,255,255,0.3)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                }}
            >
                <ChevronRightIcon />
            </IconButton>
        </Stack>
    );
};

const AGGREGATE_METRICS = ['games,acs,kpr,dpr,rounds,kills_total,deaths_total,assists_total,kast,adr,first_blood_total,first_deaths_total'];

function StatsByDefensivePattern(props: {
    paramObject: any,
    defensePattern: string | undefined,
    setDefensePattern: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'],
            side: 'Defender',
            groupby:'defense_pattern_short' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsByEcoType(r.map((pat: any) => {return {...pat, rounds: pat.rounds/5}}))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setDefensePattern(props.defensePattern === stats.defense_pattern_short ? undefined : stats.defense_pattern_short)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.defense_pattern_short ? stats.defense_pattern_short : 'No pattern')}</Title>} selected={stats.defense_pattern_short === props.defensePattern}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Defensive Pattern'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Defensive Pattern', statsByEcoType, 'defense_pattern_short')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.defense_pattern_short}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByAttackingPattern(props: {
    paramObject: any,
    attackPattern: string | undefined,
    setAttackPattern: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'],
            side: 'Attacker',
            groupby:'attack_pattern_short' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsByEcoType(r.map((pat: any) => {return {...pat, rounds: pat.rounds/5}}))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setAttackPattern(props.attackPattern === stats.attack_pattern_short ? undefined : stats.attack_pattern_short)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.attack_pattern_short ? stats.attack_pattern_short : 'No pattern')}</Title>} selected={stats.attack_pattern_short === props.attackPattern}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Attacking Pattern'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Attacking Pattern', statsByEcoType, 'attack_pattern_short')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.attack_pattern_short}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByEcoType(props: {
    paramObject: any,
    economy: string | undefined,
    setEconomy: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 1;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'round_eco_type_no_bonus' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsByEcoType(r)
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setEconomy(props.economy === stats.round_eco_type_no_bonus ? undefined : stats.round_eco_type_no_bonus)} stats={stats} title={undefined} decoration={<Title>{capitalize(stats.round_eco_type_no_bonus)}</Title>} selected={stats.round_eco_type_no_bonus === props.economy}/>
    }

    const visibleItems = statsByEcoType.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Economy'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Economy', statsByEcoType, 'round_eco_type_no_bonus')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.round_eco_type_no_bonus}-${currentIndex + index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByEcoType.length > visibleCount && (
                <CarouselNav
                    currentIndex={currentIndex}
                    totalItems={statsByEcoType.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByEcoType.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}

function StatsByBombSite(props: {
    paramObject: any,
    bombSite: string | undefined,
    setBombSite: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByEcoType, setStatsByEcoType] = useState<any[]>([]);
    useEffect(() => {
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'bomb_site' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsByEcoType(r.map((site: any) => {
                return {...site, rounds: site.rounds / 5}
            }))
        );
    }, [props.paramObject.watermark]);

    const statsBlock = (stats: any) => {
        return <StatsRow onClick={() => props.setBombSite(props.bombSite === stats.bomb_site ? undefined : stats.bomb_site)} stats={stats} decoration={<Title>{capitalize(stats.bomb_site ? stats.bomb_site : 'Not Planted')}</Title>} title={undefined} selected={stats.bomb_site === props.bombSite}/>
    }

    return <NeonCard sx={{width:'100%', height:'100%', display: 'flex', flexDirection: 'column'}}>
        <NeonCardHeader title={'Site'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Site', statsByEcoType, 'bomb_site')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{
            flex: 1,
            width:'100%',
            flexDirection:'column',
            alignItems:'flex-start',
            justifyContent:'flex-start',
            overflow: 'hidden',
            padding: '8px 16px',
        }}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            height:'100%',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingRight: '4px',
                            ...scrollable
                        }}
                    >
            {
                statsByEcoType.map((r, index) => (
                    <React.Fragment key={`${r.bomb_site}-${index}`}>
                        {statsBlock(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
        </NeonCardContent>
    </NeonCard>
}

function StatsBySide(props: {paramObject: any, side: string|undefined, setSide: React.Dispatch<SetStateAction<undefined|string>>, onShowDetails: (title: string, rows: any[], groupby: string) => void}){
    const ctx = useReferentialContext();
    const [statsBySide, setStatsBySide] = useState<any[]>([]);
    useEffect(() => {
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'side' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsBySide(r.map(
                (team_stats: any) => {return {...team_stats, kpr: team_stats.kpr * 5, rounds: team_stats.rounds / 5, dpr: team_stats.dpr * 5}}
            ))
        );
    }, [props.paramObject.watermark]);

    const sideStats = (stats: any) => {
        return <StatsRow onClick={() => props.setSide(props.side === stats.side ? undefined : stats.side)} stats={stats} decoration={<Title>{capitalize(stats.side)}</Title>} title={undefined} selected={stats.side === props.side}/>
    }

    return <NeonCard sx={{width:'100%', height:'100%', display: 'flex', flexDirection: 'column'}}>
        <NeonCardHeader title={'Side'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Side', statsBySide, 'side')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{
            flex: 1,
            width:'100%',
            flexDirection:'column',
            alignItems:'flex-start',
            justifyContent:'flex-start',
            overflow: 'hidden',
            padding: '8px 16px',
        }}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            height:'100%',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            paddingRight: '4px',
                            ...scrollable
                        }}
                    >
            {
                statsBySide.map((r, index) => (
                    <React.Fragment key={`${props.paramObject.map}-${index}-${r.side}`}>
                        {sideStats(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
        </NeonCardContent>
    </NeonCard>
}

function StatsByAgent(props: {
    paramObject: any,
    agent: string | undefined,
    setAgent: React.Dispatch<SetStateAction<undefined | string>>,
    onShowDetails: (title: string, rows: any[], groupby: string) => void,
}) {
    const ctx = useReferentialContext();
    const [statsByAgent, setStatsByAgent] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const visibleCount = 2;

    useEffect(() => {
        setCurrentIndex(0);
        ctx.reckonApi.scrimRoundSummaries.aggregate({...props.paramObject, ordering: ['-rounds'], groupby:'agent' as any, aggregates: ['win__avg'],
            metrics: AGGREGATE_METRICS}).then(
            r => setStatsByAgent(r)
        );
    }, [props.paramObject.watermark]);

    const agentStats = (stats: any) => {
        return <StatsRow onClick={() => props.setAgent(props.agent === stats.agent ? undefined : stats.agent)} stats={stats} title={<AgentIcon agent_name={stats.agent}/>} decoration={<Title>{stats.agent}</Title>} selected={stats.agent === props.agent}/>
    }

    const visibleItems = statsByAgent.slice(currentIndex, currentIndex + visibleCount);
    const itemHeight = 128;

    return <NeonCard sx={{width:'100%', height:'100%'}}>
        <NeonCardHeader title={'Agents'} action={<NeonButton variant="outlined" size="small" onClick={() => props.onShowDetails('Agents', statsByAgent, 'agent')} sx={{fontSize: '0.7rem', px: 1, py: 0.25, minWidth: 0}}>Details</NeonButton>}/>
        <NeonCardContent sx={{height:'100%', width:'100%', flexDirection:'column',
                    alignItems:'center', justifyContent:'center'}}>
            <Stack direction={'column'} spacing={1}
                        sx={{
                            alignItems: "center",
                            justifyContent: "flex-start",
                            display: "flex",
                            width: "100%",
                            minHeight: `${visibleCount * itemHeight}px`,
                        }}
                    >
            {
                visibleItems.map((r, index) => (
                    <React.Fragment key={`${r.agent}-${currentIndex + index}`}>
                        {agentStats(r)}
                    </React.Fragment>
                ))
            }
            </Stack>
            {statsByAgent.length > visibleCount && (
                <CarouselNav
                    currentIndex={currentIndex}
                    totalItems={statsByAgent.length}
                    visibleCount={visibleCount}
                    onPrev={() => setCurrentIndex(Math.max(0, currentIndex - visibleCount))}
                    onNext={() => setCurrentIndex(Math.min(statsByAgent.length - visibleCount, currentIndex + visibleCount))}
                />
            )}
        </NeonCardContent>
    </NeonCard>
}
